import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchUsers, fetchPendingUsers, approveUser, rejectUser, fetchUserById, clearSelectedUser, createUser, updateUser, deleteUser, selectUsers, selectUsersLoading, selectUsersError, selectSelectedUser } from '../../store/slices/usersSlice';
import { fetchCompanies, selectCompanies } from '../../store/slices/companiesSlice';
import type { AdminUserRecord } from '../../services/api';
import { Users, AlertCircle, Plus, X } from 'lucide-react';
import Button from '../ui/Button';
import './UsersPage.css';
import '../ui/Modal.css';

const UsersPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const users = useAppSelector(selectUsers);
    const isLoading = useAppSelector(selectUsersLoading);
    const error = useAppSelector(selectUsersError);
    const selectedUser = useAppSelector(selectSelectedUser);
    const companies = useAppSelector(selectCompanies);

    const [isAddUserModalOpen, setIsAddUserModalOpen] = React.useState(false);
    const [newUserForm, setNewUserForm] = React.useState({
        email: '',
        password: '',
        role: 'client',
        companyId: '',
        firstName: '',
        lastName: '',
        phoneNumber: ''
    });
    const [createError, setCreateError] = React.useState<string | null>(null);
    const [isCreating, setIsCreating] = React.useState(false);

    const [isEditUserModalOpen, setIsEditUserModalOpen] = React.useState(false);
    const [editUserForm, setEditUserForm] = React.useState({
        id: '',
        email: '',
        role: 'client',
        companyId: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
    });
    const [editError, setEditError] = React.useState<string | null>(null);
    const [isUpdating, setIsUpdating] = React.useState(false);

    const [viewMode, setViewMode] = React.useState<'all' | 'pending'>('all');

    const getCompanyName = (companyId: string) => {
        const company = companies.find(c => c.id === companyId);
        return company ? company.legalName : companyId || 'N/A';
    };

    useEffect(() => {
        if (viewMode === 'all') {
            dispatch(fetchUsers());
        } else {
            dispatch(fetchPendingUsers());
        }
    }, [dispatch, viewMode]);

    useEffect(() => {
        if (companies.length === 0) {
            dispatch(fetchCompanies());
        }
    }, [dispatch, companies.length]);

    if (isLoading) {
        return (
            <div className="users-page loading">
                <div className="spinner" />
                <p>Loading users...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="users-page error">
                <AlertCircle size={48} className="error-icon" />
                <h2>Failed to load users</h2>
                <p>{error}</p>
                <button onClick={() => viewMode === 'all' ? dispatch(fetchUsers()) : dispatch(fetchPendingUsers())} className="retry-btn">
                    Retry
                </button>
            </div>
        );
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewUserForm(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreateError(null);
        setIsCreating(true);

        try {
            await dispatch(createUser(newUserForm)).unwrap();
            setIsAddUserModalOpen(false);
            setNewUserForm({
                email: '',
                password: '',
                role: 'client',
                companyId: '',
                firstName: '',
                lastName: '',
                phoneNumber: ''
            });
        } catch (err: any) {
            setCreateError(err.message || 'Failed to create user');
        } finally {
            setIsCreating(false);
        }
    };

    const handleOpenEditModal = (user: AdminUserRecord) => {
        setEditUserForm({
            id: user.id,
            email: user.email,
            role: user.role,
            companyId: user.companyId || '',
            firstName: user.firstName,
            lastName: user.lastName,
            phoneNumber: user.phoneNumber || '',
        });
        setEditError(null);
        setIsEditUserModalOpen(true);
    };

    const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const parsedValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
        setEditUserForm(prev => ({ ...prev, [name]: parsedValue }));
    };

    const handleUpdateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setEditError(null);
        setIsUpdating(true);

        const { id, ...userData } = editUserForm;

        try {
            await dispatch(updateUser({ id, userData })).unwrap();
            setIsEditUserModalOpen(false);
        } catch (err: any) {
            setEditError(err.message || 'Failed to update user');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteUser = async (user: AdminUserRecord) => {
        if (window.confirm(`Are you sure you want to delete user ${user.firstName} ${user.lastName}? This action cannot be undone.`)) {
            try {
                await dispatch(deleteUser(user.id)).unwrap();
            } catch (err: any) {
                alert(err.message || 'Failed to delete user');
            }
        }
    };

    return (
        <div className="users-page">
            <div className="page-header">
                <div className="title-section">
                    <Users size={24} />
                    <h1>Users Management</h1>
                </div>
                <div className="actions-section">
                    <div className="view-toggle">
                        <button
                            className={`toggle-btn ${viewMode === 'all' ? 'active' : ''}`}
                            onClick={() => setViewMode('all')}
                        >
                            All Users
                        </button>
                        <button
                            className={`toggle-btn ${viewMode === 'pending' ? 'active' : ''}`}
                            onClick={() => setViewMode('pending')}
                        >
                            Pending Configs
                        </button>
                    </div>
                    <Button variant="primary" onClick={() => setIsAddUserModalOpen(true)}>
                        <Plus size={16} />
                        Add User
                    </Button>
                </div>
            </div>

            <div className="users-table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Company</th>
                            <th>Phone Number</th>
                            <th>Created At</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="empty-state">
                                    <div className="empty-state-content">
                                        <Users size={48} strokeWidth={1} />
                                        <p>No users found matching your selection.</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            users.map((user) => (
                                <tr key={user.id} className="table-row-premium">
                                    <td>
                                        <div className="user-name-cell">
                                            <div className="user-avatar-small">
                                                {user.firstName[0]}{user.lastName[0]}
                                            </div>
                                            <div className="user-info-text">
                                                <span className="user-name">{user.firstName} {user.lastName}</span>
                                                <span className="user-id-sub">#{user.id.slice(0, 8)}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="email-cell">{user.email}</div>
                                    </td>
                                    <td>
                                        <span className={`role-badge ${user.role}`}>
                                            {user.role === 'company_admin' ? 'Company Admin' : user.role}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${user.status}`}>
                                            {user.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="company-cell">{getCompanyName(user.companyId)}</div>
                                    </td>
                                    <td>{user.phoneNumber || '—'}</td>
                                    <td>
                                        <div className="date-cell">
                                            {new Date(user.createdAt).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                            })}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-actions">
                                            <div className="action-button-group">
                                                <button
                                                    className="action-icon-btn view"
                                                    title="View Profile"
                                                    onClick={() => dispatch(fetchUserById(user.id))}
                                                >
                                                    View
                                                </button>
                                                <button
                                                    className="action-icon-btn edit"
                                                    title="Edit User"
                                                    onClick={() => handleOpenEditModal(user)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="action-icon-btn delete"
                                                    title="Delete User"
                                                    onClick={() => handleDeleteUser(user)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                            {user.status === 'pending' && (
                                                <div className="approval-actions">
                                                    <button
                                                        className="approve-btn"
                                                        onClick={() => dispatch(approveUser(user.id))}
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        className="reject-btn"
                                                        onClick={() => dispatch(rejectUser(user.id))}
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {isAddUserModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-container modal-lg">
                        <div className="modal-header">
                            <h2>Add New User</h2>
                            <button className="modal-close-btn" onClick={() => setIsAddUserModalOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleCreateUser} className="modal-form">
                            {createError && (
                                <div className="error-alert">
                                    <AlertCircle size={16} />
                                    <span>{createError}</span>
                                </div>
                            )}

                            <div className="form-grid">
                                <div className="form-group">
                                    <label htmlFor="firstName">First Name</label>
                                    <input type="text" id="firstName" name="firstName" required value={newUserForm.firstName} onChange={handleInputChange} placeholder="e.g. John" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="lastName">Last Name</label>
                                    <input type="text" id="lastName" name="lastName" required value={newUserForm.lastName} onChange={handleInputChange} placeholder="e.g. Doe" />
                                </div>
                                <div className="form-group full-width">
                                    <label htmlFor="email">Email Address</label>
                                    <input type="email" id="email" name="email" required value={newUserForm.email} onChange={handleInputChange} placeholder="e.g. john.doe@example.com" />
                                </div>
                                <div className="form-group full-width">
                                    <label htmlFor="password">Password</label>
                                    <input type="password" id="password" name="password" required value={newUserForm.password} onChange={handleInputChange} placeholder="Minimum 8 characters" />
                                </div>
                                <div className="form-group full-width">
                                    <label htmlFor="phoneNumber">Phone Number</label>
                                    <input type="tel" id="phoneNumber" name="phoneNumber" value={newUserForm.phoneNumber} onChange={handleInputChange} placeholder="e.g +234 800 000 0000" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="role">User Role</label>
                                    <select id="role" name="role" required value={newUserForm.role} onChange={handleInputChange}>
                                        <option value="client">Client</option>
                                        <option value="admin">Admin</option>
                                        <option value="company_admin">Company Admin</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="companyId">Company</label>
                                    <select
                                        id="companyId"
                                        name="companyId"
                                        required
                                        value={newUserForm.companyId}
                                        onChange={handleInputChange}
                                    >
                                        <option value="" disabled>Select a company</option>
                                        {companies.map(company => (
                                            <option key={company.id} value={company.id}>
                                                {company.legalName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <Button type="button" variant="outline" onClick={() => setIsAddUserModalOpen(false)} disabled={isCreating}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="primary" loading={isCreating}>
                                    Create User
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {selectedUser && (
                <div className="modal-overlay">
                    <div className="modal-container modal-md">
                        <div className="modal-header">
                            <h2>User Profile</h2>
                            <button className="modal-close-btn" onClick={() => dispatch(clearSelectedUser())}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="details-modal-container">
                            <div className="details-hero">
                                <div className="details-avatar-large">
                                    <Users size={32} />
                                </div>
                                <div className="details-title-section">
                                    <h1>{selectedUser.firstName} {selectedUser.lastName}</h1>
                                    <span className={`status-badge ${selectedUser.status}`}>
                                        {selectedUser.status}
                                    </span>
                                </div>
                            </div>
                            <div className="details-grid-premium">
                                <div className="detail-card">
                                    <label>Email Address</label>
                                    <p>{selectedUser.email}</p>
                                </div>
                                <div className="detail-card">
                                    <label>Phone Number</label>
                                    <p>{selectedUser.phoneNumber || 'Not provided'}</p>
                                </div>
                                <div className="detail-card">
                                    <label>User Role</label>
                                    <p><span className={`role-badge ${selectedUser.role}`}>{selectedUser.role}</span></p>
                                </div>
                                <div className="detail-card">
                                    <label>Company</label>
                                    <p>{getCompanyName(selectedUser.companyId)}</p>
                                </div>
                                <div className="detail-card">
                                    <label>Member Since</label>
                                    <p>{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                                </div>
                                <div className="detail-card">
                                    <label>Last Updated</label>
                                    <p>{new Date(selectedUser.updatedAt).toLocaleDateString()}</p>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <Button type="button" variant="outline" onClick={() => dispatch(clearSelectedUser())}>
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {isEditUserModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-container modal-lg">
                        <div className="modal-header">
                            <h2>Edit User</h2>
                            <button className="modal-close-btn" onClick={() => setIsEditUserModalOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateUser} className="modal-form">
                            {editError && (
                                <div className="error-alert">
                                    <AlertCircle size={16} />
                                    <span>{editError}</span>
                                </div>
                            )}

                            <div className="form-grid">
                                <div className="form-group">
                                    <label htmlFor="editFirstName">First Name</label>
                                    <input type="text" id="editFirstName" name="firstName" required value={editUserForm.firstName} onChange={handleEditInputChange} placeholder="e.g. John" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="editLastName">Last Name</label>
                                    <input type="text" id="editLastName" name="lastName" required value={editUserForm.lastName} onChange={handleEditInputChange} placeholder="e.g. Doe" />
                                </div>
                                <div className="form-group full-width">
                                    <label htmlFor="editEmail">Email Address</label>
                                    <input type="email" id="editEmail" name="email" required value={editUserForm.email} onChange={handleEditInputChange} placeholder="e.g. john.doe@example.com" />
                                </div>
                                <div className="form-group full-width">
                                    <label htmlFor="editPhoneNumber">Phone Number</label>
                                    <input type="tel" id="editPhoneNumber" name="phoneNumber" value={editUserForm.phoneNumber} onChange={handleEditInputChange} placeholder="e.g +234 800 000 0000" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="editRole">User Role</label>
                                    <select id="editRole" name="role" required value={editUserForm.role} onChange={handleEditInputChange}>
                                        <option value="client">Client</option>
                                        <option value="admin">Admin</option>
                                        <option value="company_admin">Company Admin</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="editCompanyId">Company</label>
                                    <select
                                        id="editCompanyId"
                                        name="companyId"
                                        required
                                        value={editUserForm.companyId}
                                        onChange={handleEditInputChange}
                                    >
                                        <option value="" disabled>Select a company</option>
                                        {companies.map(company => (
                                            <option key={company.id} value={company.id}>
                                                {company.legalName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <Button type="button" variant="outline" onClick={() => setIsEditUserModalOpen(false)} disabled={isUpdating}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="primary" loading={isUpdating}>
                                    Save Changes
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersPage;
